import { endOfDay, startOfDay } from "@clipboard-health/date-time";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { usePaginatedWorkerShifts } from "@src/appV2/redesign/WorkerShifts/api/usePaginatedWorkerShifts";
import { useCallback, useMemo } from "react";

import {
  getScholarshipStorageKey,
  SCHOLARSHIP_LOCAL_DEADLINE,
  SCHOLARSHIP_LOCAL_START_DATE,
  SCHOLARSHIP_REQUIRED_SHIFTS,
  SCHOLARSHIP_RESPONSES,
  type ScholarshipResponse,
} from "./constants";

interface ScholarshipProgress {
  isEnrolled: boolean;
  completedShifts: number;
  shiftsRemaining: number;
  isPastDeadline: boolean;
  shouldShowStatusPage: boolean;
  isLoading: boolean;
  refetchShiftCount: () => Promise<void>;
}

export function useScholarshipProgress(
  workerId: string,
  workerTimezone: string
): ScholarshipProgress {
  const responseKey = getScholarshipStorageKey(workerId);

  const [scholarshipResponse] = useLocalStorage<ScholarshipResponse | undefined>(
    responseKey,
    undefined
  );

  const isEnabled = useCbhFlag(CbhFeatureFlag.CONCORDE_SCHOLARSHIP, {
    defaultValue: false,
  });

  const isEnrolled = scholarshipResponse === SCHOLARSHIP_RESPONSES.INTERESTED;
  const isPastDeadline = useMemo(
    () =>
      new Date().getTime() >
      endOfDay(new Date(SCHOLARSHIP_LOCAL_DEADLINE), { timeZone: workerTimezone }).getTime(),
    [workerTimezone]
  );

  const startDateUtc = useMemo(
    () => startOfDay(new Date(SCHOLARSHIP_LOCAL_START_DATE), { timeZone: workerTimezone }),
    [workerTimezone]
  );
  const now = useMemo(() => new Date(), []);

  const shiftsQuery = usePaginatedWorkerShifts(
    {
      workerId,
      pageSize: 1,
      fields: { shift: ["schedule.startAt"] },
      include: [],
      filter: {
        start: {
          gt: startDateUtc,
          lt: now,
        },
        deleted: false,
      },
      sortBy: "schedule.startAt",
      sortOrder: "desc",
    },
    {
      enabled: isEnabled && isEnrolled && !isPastDeadline,
    }
  );

  const completedShifts = shiftsQuery.data?.pages[0]?.meta?.totalCount ?? 0;
  const shiftsRemaining = Math.max(0, SCHOLARSHIP_REQUIRED_SHIFTS - completedShifts);

  const shouldShowStatusPage = useMemo(() => {
    if (!isEnabled) {
      return false;
    }

    if (!isEnrolled) {
      return false;
    }

    if (isPastDeadline) {
      return false;
    }

    if (shiftsQuery.isLoading) {
      return false;
    }

    if (completedShifts > SCHOLARSHIP_REQUIRED_SHIFTS) {
      return false;
    }

    return true;
  }, [isEnabled, isEnrolled, isPastDeadline, completedShifts, shiftsQuery.isLoading]);

  const { refetch } = shiftsQuery;
  const refetchShiftCount = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    isEnrolled,
    completedShifts,
    shiftsRemaining,
    isPastDeadline,
    shouldShowStatusPage,
    isLoading: shiftsQuery.isLoading,
    refetchShiftCount,
  };
}
